import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import type { Logger } from '../../../composables/useGtmLogRequest'
import { getUrlPrefix } from '../../../lang/locales'
import { langFallback$Fetch } from '../../../lang/utils/langfetch'
import { SearchFilters } from '../schemas'
import {
  SearchCountsResults,
  SearchNonPriceResultsResponse,
  SearchPriceFacetsResultsResponse,
} from './types'

const PRICE_SEARCH_ENDPOINT = '/search2/price'
const NON_PRICE_SEARCH_ENDPOINT = '/search2/postgres-nonprice'

export class CountsService {
  logger: Logger
  constructor(options: { logger: Logger }) {
    this.logger = options.logger
  }

  async fetchPriceSearchCounts(
    langCode: string,
    searchFilters: SearchFilters,
    fetchOptions?: any,
  ): Promise<SearchCountsResults | undefined> {
    const params = this.getQueryString(searchFilters)
    const langPrefix = getUrlPrefix(langCode)
    const url = `/_/fallback${langPrefix}${PRICE_SEARCH_ENDPOINT}/?${params}`
    try {
      const gtmLogRequest = useGtmLogRequest(
        'CountsService-fetchPriceSearchCounts',
        this.logger,
      )
      gtmLogRequest.markStart()
      const response = await $fetch<SearchPriceFacetsResultsResponse>(
        url,
        fetchOptions,
      )
      gtmLogRequest.markEnd()
      return this.formatPriceResults(response)
    } catch (error) {
      if (
        // @ts-ignore
        error?.cause instanceof DOMException &&
        error?.cause.name === 'AbortError'
      ) {
        // Ignore AbortError triggered by AbortController
      } else {
        throw error // Rethrow other errors to be handled by the caller
      }
    }
    return undefined
  }

  async fetchNonPriceSearchCounts(
    langCode: string,
    searchFilters: SearchFilters,
    fetchOptions?: any,
  ): Promise<SearchCountsResults | undefined> {
    const params = searchFilters ? `?${this.getQueryString(searchFilters)}` : ''
    const url = `${NON_PRICE_SEARCH_ENDPOINT}/${params}`
    try {
      const gtmLogRequest = useGtmLogRequest(
        'CountsService-fetchNonPriceSearchCounts',
        this.logger,
      )
      gtmLogRequest.markStart()
      const response = await langFallback$Fetch<SearchNonPriceResultsResponse>(
        langCode,
        url,
        fetchOptions,
      )
      gtmLogRequest.markEnd()
      return this.formatNonPriceResults(response)
    } catch (error) {
      if (
        // @ts-ignore
        error?.cause instanceof DOMException &&
        error?.cause.name === 'AbortError'
      ) {
        // Ignore AbortError triggered by AbortController
      } else {
        throw error // Rethrow other errors to be handled by the caller
      }
    }
  }

  private getQueryString(searchFilters: SearchFilters): string {
    const newFilters = {
      ...searchFilters,
      limit: 0,
    }
    return this.filtersToParams(newFilters).toString() + '&counts=1'
  }

  private filtersToParams(filters: SearchFilters): URLSearchParams {
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl(filters)) {
      params.append(param.filterName, param.filterValue)
    }
    params.append('num_bins', '100')
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      } else {
        // only full bookable if bounds and no path
        params.append('bookable', '1')
      }
    }
    return params
  }

  private formatNonPriceResults(
    results: SearchNonPriceResultsResponse,
  ): SearchCountsResults {
    return {
      bookableCount: results.bookable_count,
      categoriesCounts: results.facet_category,
      hierarchyCounts: this.getHierarchiesFromSearchResults(results),
      keywordsCounts: results.facet_keywords,
      ...results,
    }
  }

  private getHierarchiesFromSearchResults(
    results: SearchNonPriceResultsResponse,
  ) {
    const hierarchies = {}
    const hierarchiesToCover = ['facet_hierarchy']
    for (const hierarchy of hierarchiesToCover) {
      for (const [key, value] of Object.entries(results[hierarchy])) {
        hierarchies[key] = value
      }
    }
    return hierarchies
  }

  private formatPriceResults(
    results: SearchPriceFacetsResultsResponse,
  ): SearchCountsResults {
    return {
      bookableCount: results.facet_all,
      categoriesCounts: results.facet_category,
      hierarchyCounts: results.facet_hierarchy,
      keywordsCounts: results.facet_keywords,
      resultMaxPrice: results.result_max_price,
      priceHistogram: results.price_histogram,
      ...results,
    }
  }
}
